import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ButtonBase from "@mui/material/ButtonBase";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';

import type { FlowComponentRenderProps, TopBarComponentConfig } from "../types";

function telHref(value?: string) {
  const dialable = value?.replace(/[^\d+]/g, "");
  return dialable ? `tel:${dialable}` : undefined;
}

const TopBarBlock: React.FC<
  FlowComponentRenderProps<TopBarComponentConfig>
> = ({ config, onAction }) => {
  const [logoImageFailed, setLogoImageFailed] = useState(false);
  const logoLabel = config.logoLabel?.trim() ?? "";
  const plainLogoLabel = logoLabel
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const phoneLabel = config.phoneLabel?.trim() ?? "";
  const href = telHref(phoneLabel);
  // Content-driven height: py (8–12px) + logo/call. Size is real (compact < standard < hero).
  const sizePreset = config.size ?? "compact";
  const compact = sizePreset === "compact";
  const isHero = sizePreset === "hero";
  const logoMaxHeight = isHero ? 48 : compact ? 40 : 44;
  const logoWidth = isHero ? 180 : compact ? 140 : 160;
  const callSize = isHero ? 36 : compact ? 28 : 32;
  const verticalPad = isHero ? 1.5 : compact ? 1 : 1.25;
  const isCard = config.style === "card";
  const alignment = config.align ?? "left";

  useEffect(() => {
    setLogoImageFailed(false);
  }, [config.logoUrl]);

  return (
    <Box
      sx={{
        alignItems: "center",
        bgcolor: "var(--cx-brand-surface)",
        border: isCard
          ? "1px solid var(--cx-brand-border)"
          : "1px solid transparent",
        borderRadius: isCard ? 2.5 : 0,
        color: "var(--cx-brand-text)",
        display: "flex",
        flexDirection: alignment === "right" ? "row-reverse" : "row",
        justifyContent: alignment === "center" ? "center" : "space-between",
        px: 2,
        py: verticalPad,
        width: "100%",
        '[data-cx-color-scheme="dark"] &': {
          backdropFilter: 'blur(16px)',
          background: 'color-mix(in srgb, var(--cx-brand-surface) 68%, transparent)',
          borderBottom: '1px solid var(--cx-brand-border)',
          py: 1.5,
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", minWidth: 0 }}
      >
        {config.logoUrl && !logoImageFailed ? (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexShrink: 0,
              height: logoMaxHeight,
              width: logoWidth,
              maxWidth: "100%",
              '[data-cx-color-scheme="dark"] &': { display: 'none' },
            }}
          >
            <Box
              alt={plainLogoLabel}
              component="img"
              onError={() => setLogoImageFailed(true)}
              src={config.logoUrl}
              sx={{
                display: "block",
                height: "100%",
                maxHeight: logoMaxHeight,
                maxWidth: "100%",
                objectFit: "contain",
                objectPosition: "left center",
                width: "100%",
              }}
            />
          </Box>
        ) : logoLabel ? (
          <Box
            sx={{
              color: "var(--cx-brand-text)",
              fontFamily: config.logoFont?.trim()
                ? config.logoFont.includes(",")
                  ? config.logoFont
                  : `${config.logoFont}, "Public Sans Variable", Arial, sans-serif`
                : 'Barlow, "Public Sans Variable", Arial, sans-serif',
              fontSize: compact ? 16 : isHero ? 22 : 18,
              fontWeight: 800,
              lineHeight: 1.1,
              maxWidth: 240,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {plainLogoLabel}
          </Box>
        ) : null}
        {config.logoUrl && !logoImageFailed && plainLogoLabel ? (
          <Box
            aria-hidden="true"
            sx={{
              alignItems: 'center',
              bgcolor: 'var(--cx-brand-primary)',
              borderRadius: 999,
              color: 'var(--cx-brand-primary-contrast)',
              display: 'none',
              fontSize: 13,
              fontWeight: 600,
              height: 36,
              justifyContent: 'center',
              letterSpacing: '-0.04em',
              textTransform: 'lowercase',
              width: 36,
              '[data-cx-color-scheme="dark"] &': { display: 'flex' },
            }}
          >
            {plainLogoLabel.split(/\s+/)[0]}
          </Box>
        ) : null}
        {config.logoUrl && !logoImageFailed && plainLogoLabel ? (
          <Box
            sx={{
              color: 'var(--cx-brand-text)',
              display: 'none',
              fontSize: 18,
              fontWeight: 500,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              '[data-cx-color-scheme="dark"] &': { display: 'block' },
            }}
          >
            {plainLogoLabel}
          </Box>
        ) : null}
      </Stack>

      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'color-mix(in srgb, var(--cx-brand-primary) 12%, transparent)',
          borderRadius: 999,
          color: 'var(--cx-brand-primary)',
          display: 'none',
          fontSize: 12,
          fontWeight: 600,
          gap: 0.625,
          px: 1.25,
          py: 0.75,
          whiteSpace: 'nowrap',
          '[data-cx-color-scheme="dark"] &': { display: 'flex' },
        }}
      >
        <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16 }} />
        Service complete
      </Box>

      {phoneLabel ? (
        <ButtonBase
          aria-label={`Call ${phoneLabel}`}
          component={href ? "a" : "button"}
          href={href}
          onClick={() => {
            onAction?.({
              componentId: config.id,
              label: "Call",
              phone: phoneLabel,
              type: "contact",
            });
          }}
          sx={{
            alignItems: "center",
            bgcolor: "var(--cx-brand-primary)",
            border: "1px solid var(--cx-brand-primary)",
            borderRadius: 999,
            color: "var(--cx-brand-primary-contrast)",
            display: "flex",
            flexShrink: 0,
            height: callSize,
            justifyContent: "center",
            width: callSize,
            '[data-cx-color-scheme="dark"] &': {
              display: 'none',
            },
            "&:hover": {
              bgcolor: "var(--cx-brand-primary-dark)",
            },
          }}
          type={href ? undefined : "button"}
        >
          <PhoneRoundedIcon sx={{ fontSize: compact ? 16 : 18 }} />
        </ButtonBase>
      ) : null}
    </Box>
  );
};

export default TopBarBlock;
